import React from 'react'
import useSwr from 'swr'
import { API_URL } from '@/utils/constants'
import { FRBTC_ID, frbtcMidPriceScaled, type Pool } from './amm'
import { strictFetcher } from './strict-fetcher'

/// One row of `GET /alkane/assets` — the per-ASSET aggregate (an asset that
/// trades in eleven pools is one row here, priced in USD).
export interface AssetRow {
	id: string
	name: string | null
	icon: string | null
	price_usd: number | null
	change_24h_pct: number | null
	/// Only present when pizza.fun's all-pools index covers the asset.
	volume_24h_usd: number | null
	liquidity_usd: number
	/// op-101 supply × price, when the server has the supply cached (a warm
	/// pass covers the top assets; a detail visit fills the rest). Older pods
	/// omit the field entirely — treat absent as null and render NO market-cap
	/// chip, never a fabricated figure.
	market_cap_usd?: number | null
	/// Holder count from the same never-fetch server snapshot as the market
	/// cap: present once the stats pipeline has learned it, absent/null until
	/// then. Enables the "Most held" lens; rows without it sink, not lie.
	holders?: number | null
	pools: number
	/// How the price above was arrived at, weakest claim last:
	///
	/// - `pizza`        — the all-pools USD index carries this asset directly.
	/// - `derived`      — read off this asset's own frBTC pool reserves.
	/// - `derived-2hop` — no frBTC pool of its own; priced through ONE hop via a
	///                    counterparty that has one. A real price with a longer
	///                    chain of assumptions behind it, so the UI says so
	///                    rather than presenting it as the same kind of number.
	///
	/// Older API pods never send `derived-2hop`; those assets simply carried a
	/// null price instead, which is why nothing may treat the absence of this
	/// value as meaning anything.
	price_source: 'pizza' | 'derived' | 'derived-2hop'
}

interface AssetsResponse {
	assets: AssetRow[]
	height: number | null
	btc_usd: number | null
}

/// Client-side fallback: derive the same aggregate rows from the pool list.
/// This is what renders when `/alkane/assets` hasn't answered (older API pod,
/// first paint before the fetch) — the picker degrades to derived pricing and
/// no volume column instead of degrading to a blank dialog.
function assetsFromPools(pools: Pool[], btcUsd: number | null): AssetRow[] {
	const byId = new Map<string, AssetRow>()
	for (const p of pools) {
		for (const t of [p.token0, p.token1]) {
			const prev = byId.get(t.id)
			if (!prev) {
				byId.set(t.id, {
					id: t.id,
					name: t.name,
					icon: t.icon,
					price_usd: null,
					change_24h_pct: null,
					volume_24h_usd: null,
					liquidity_usd: 0,
					pools: 1,
					price_source: 'derived'
				})
			} else {
				prev.pools += 1
				if (!prev.name && t.name) {
					prev.name = t.name
				}
			}
		}
	}
	// Price via the deepest frBTC route, then value each pool at the mids of
	// its two sides — a side with no mid contributes nothing, so unpriceable
	// tokens can't inflate the ranking.
	const usdOfRaw = (id: string, raw: bigint): number | null => {
		if (btcUsd === null) {
			return null
		}
		const mid = frbtcMidPriceScaled(pools, id)
		if (mid === null) {
			return null
		}
		return (Number(raw * mid) / 1e24) * btcUsd
	}
	for (const row of byId.values()) {
		const mid = frbtcMidPriceScaled(pools, row.id)
		if (mid !== null && btcUsd !== null) {
			row.price_usd = (Number(mid) / 1e16) * btcUsd
		}
	}
	for (const p of pools) {
		const v0 = usdOfRaw(p.token0.id, BigInt(p.reserve0)) ?? 0
		const v1 = usdOfRaw(p.token1.id, BigInt(p.reserve1)) ?? 0
		const total = v0 + v1
		if (total <= 0) {
			continue
		}
		const r0 = byId.get(p.token0.id)
		const r1 = byId.get(p.token1.id)
		if (r0) {
			r0.liquidity_usd += total
		}
		if (r1) {
			r1.liquidity_usd += total
		}
	}
	return Array.from(byId.values()).sort((a, b) => b.liquidity_usd - a.liquidity_usd)
}

/// The tradable-asset registry, USD-priced, with the pools-derived fallback
/// folded in so callers always get rows when the pool list has them.
export default function useAssets(pools: Pool[], btcUsd: number | null) {
	const { data, isLoading } = useSwr<AssetsResponse>(`${API_URL}/alkane/assets`, strictFetcher, {
		refreshInterval: 20_000,
		revalidateOnFocus: true
	})
	const serverRows = Array.isArray(data?.assets) ? data.assets : null
	const rows = React.useMemo(() => {
		const base = serverRows && serverRows.length > 0 ? serverRows : assetsFromPools(pools, btcUsd)
		// frBTC has no meaningful "price change" (it IS the unit) but should
		// still carry the BTC price when the server hasn't stamped one. Copy —
		// the server rows live in SWR's cache and must not be mutated.
		return base.map((r) =>
			r.id === FRBTC_ID && r.price_usd === null && btcUsd !== null ? { ...r, price_usd: btcUsd } : r
		)
	}, [serverRows, pools, btcUsd])
	return { assets: rows, fromServer: serverRows !== null, isLoading }
}
